import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { auth as apiAuth, type ApiUser } from '@/utils/api';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<ApiUser | null>(null);
  const loading = ref(false);
  const initialized = ref(false);

  const isLoggedIn  = computed(() => user.value !== null);
  const isUser      = computed(() => !!user.value);
  const isManager   = computed(() => ['manager', 'admin', 'super_admin'].includes(user.value?.role ?? ''));
  const isAdmin     = computed(() => ['admin', 'super_admin'].includes(user.value?.role ?? ''));
  const isSuperAdmin= computed(() => user.value?.role === 'super_admin');

  async function init() {
    if (initialized.value) return;
    initialized.value = true;
    try {
      user.value = await apiAuth.me();
    } catch {
      // 1er /auth/me raté : peut être un cold start (cookie store pas encore prêt côté
      // WebView Android) plutôt qu'une vraie déconnexion. On retente une fois avant de conclure.
      try {
        user.value = await apiAuth.me();
      } catch {
        // pas connecté, OK
      }
    }
  }

  async function login(usernameOrEmail: string, password: string) {
    loading.value = true;
    try {
      user.value = await apiAuth.login(usernameOrEmail, password);
    } finally { loading.value = false; }
  }

  async function register(username: string, email: string, password: string) {
    loading.value = true;
    try {
      user.value = await apiAuth.register(username, email, password);
    } finally { loading.value = false; }
  }

  async function logout() {
    try { await apiAuth.logout(); } catch {}
    user.value = null;
  }

  async function changePassword(current: string, next: string) {
    await apiAuth.changePassword(current, next);
    if (user.value) user.value.must_change_password = false;
  }

  return {
    user, loading, initialized,
    isLoggedIn, isUser, isManager, isAdmin, isSuperAdmin,
    init, login, register, logout, changePassword,
  };
});
